import React, { useState, useMemo, useContext } from "react";
import axios from "axios";
import { toast } from "sonner";
import { User, Phone, MapPin, BadgeCheck, Pencil, Trash2, Users, CreditCard, CheckSquare, Square, Check, X as XIcon, Clock, Ban, Power, PowerOff, Stethoscope, ClipboardList } from "lucide-react";
import { printScreeningForm, printIdentityCard, printIdentityCardsBulk, printFormXI } from "./WorkmenPrintables";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Inline read-only display of registered workmen. Used on the Contractor Main Page
 * (and Admin Edit Contractor modal) directly below the "Register Workmen / Labour" widget.
 *
 * Props:
 *   workmen      : array of workman objects
 *   contractor   : parent contractor object (for ID card + screening form)
 *   siteMasters  : array of NCC-Site master records (with client_name, client_logo)
 *   onEdit(w)    : fired when pencil clicked
 *   onDelete(w)  : fired when trash clicked
 *   onChange()   : called after approve/reject so the parent can refetch the list
 *   showActions  : boolean (default true) — hide pencil/trash for read-only views
 *   emptyHint    : string — copy shown when list is empty
 */
export default function WorkmenList({ workmen = [], contractor, siteMasters = [], onEdit, onDelete, onChange, showActions = true, emptyHint = "No workmen registered yet." }) {
  // useAuth() returns null if WorkmenList is rendered outside of AdminAuthProvider (e.g. contractor portal). Safe.
  const auth = useAuth();
  const canApprove = !!auth?.can?.("approve_workmen");
  const [busyId, setBusyId] = useState("");
  const [rejectId, setRejectId] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  // De-activate flow: prompts contractor for Last Working Day
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [lwd, setLwd] = useState("");

  async function handleApprove(w) {
    setBusyId(w.id);
    try {
      await axios.post(`${API}/workmen/${w.id}/approve`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("ccdb_token")}` } });
      toast.success(`Approved · ${w.name}`);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    } finally {
      setBusyId("");
    }
  }
  async function submitReject() {
    if (!rejectId) return;
    setBusyId(rejectId);
    try {
      await axios.post(`${API}/workmen/${rejectId}/reject`, { reason: rejectReason }, { headers: { Authorization: `Bearer ${localStorage.getItem("ccdb_token")}` } });
      toast.success("Rejected");
      setRejectId(""); setRejectReason("");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally {
      setBusyId("");
    }
  }

  // ----- Active / De-Active toggle (contractor self OR admin) -----
  function authHeader() {
    const adminToken = localStorage.getItem("ccdb_token");
    const contractorToken = localStorage.getItem("contractor_token");
    const token = adminToken || contractorToken;
    return { Authorization: `Bearer ${token}` };
  }
  function urlSetActive(wid) {
    const adminToken = localStorage.getItem("ccdb_token");
    if (adminToken) return `${API}/workmen/${wid}/set-active`;
    return `${API}/contractor-auth/workmen/${wid}/set-active`;
  }
  async function activateWorkman(w) {
    setBusyId(w.id);
    try {
      await axios.post(urlSetActive(w.id), { is_active: true }, { headers: authHeader() });
      toast.success(`Re-activated · ${w.name}`);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not re-activate");
    } finally { setBusyId(""); }
  }
  async function submitDeactivate() {
    if (!deactivateTarget) return;
    if (!lwd) { toast.error("Last Working Day is required"); return; }
    setBusyId(deactivateTarget.id);
    try {
      await axios.post(urlSetActive(deactivateTarget.id), { is_active: false, last_working_day: lwd }, { headers: authHeader() });
      toast.success(`De-activated · ${deactivateTarget.name}`);
      setDeactivateTarget(null); setLwd("");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not de-activate");
    } finally { setBusyId(""); }
  }
  const [selected, setSelected] = useState(() => new Set());
  function siteFor(w) {
    const name = w.ncc_site_name || contractor?.ncc_site_name;
    if (!name) return null;
    return (siteMasters || []).find((s) => s.name === name) || null;
  }
  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  const allSelected = workmen.length > 0 && workmen.every((w) => selected.has(w.id));
  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(workmen.map((w) => w.id)));
  }
  const selectedList = useMemo(() => workmen.filter((w) => selected.has(w.id)), [workmen, selected]);

  // POST issue-id-card on a workman (best-effort): persists ID No + issued_at if not already.
  // Falls back to existing fields if request fails (e.g., self-mode without auth).
  async function ensureIdCard(w) {
    if (w.id_card_no) return w;
    if (!w.ncc_site_name) {
      toast.error(`Set 'Name of Project' for ${w.name} before printing ID Card`);
      return null;
    }
    try {
      const adminToken = localStorage.getItem("ccdb_token");
      const contractorToken = localStorage.getItem("contractor_token");
      let r;
      if (adminToken) {
        r = await axios.post(
          `${API}/workmen/${w.id}/issue-id-card`,
          {},
          { headers: { Authorization: `Bearer ${adminToken}` } },
        );
      } else if (contractorToken) {
        r = await axios.post(
          `${API}/contractor-auth/workmen/${w.id}/issue-id-card`,
          {},
          { headers: { Authorization: `Bearer ${contractorToken}` } },
        );
      } else {
        toast.error("Please log in to issue an ID Card.");
        return null;
      }
      return { ...w, id_card_no: r.data.id_card_no, id_card_issued_at: r.data.id_card_issued_at };
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not issue ID No.");
      return null;
    }
  }

  async function handlePrintId(w) {
    const enriched = await ensureIdCard(w);
    if (enriched) printIdentityCard(enriched, contractor, siteFor(enriched));
  }
  async function handleBulkPrint() {
    if (selectedList.length === 0) return;
    const enriched = [];
    for (const w of selectedList) {
      const e = await ensureIdCard(w);
      if (e) enriched.push(e);
    }
    if (enriched.length) printIdentityCardsBulk(enriched, contractor, siteMasters);
  }
  if (!workmen || workmen.length === 0) {
    return (
      <div
        data-testid="workmen-inline-empty"
        className="mt-3 grid place-items-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center"
      >
        <Users className="h-5 w-5 text-slate-400" />
        <div className="mt-1.5 font-mono text-[11px] text-slate-500">{emptyHint}</div>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2" data-testid="workmen-inline-list">
      {/* Bulk toolbar */}
      {showActions && (
        <div className="flex items-center justify-between gap-3 rounded-md border-2 border-ncc-navy/20 bg-ncc-tint/30 px-3 py-2" data-testid="workmen-bulk-toolbar">
          <button
            type="button"
            onClick={toggleAll}
            data-testid="workmen-bulk-select-all"
            className="inline-flex items-center gap-1.5 industrial-label text-[10px] text-ncc-navy"
          >
            {allSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
            {allSelected ? "Deselect All" : "Select All"}
            <span className="font-mono normal-case text-slate-500">({selected.size}/{workmen.length})</span>
          </button>
          <button
            type="button"
            disabled={selected.size === 0}
            onClick={handleBulkPrint}
            data-testid="workmen-bulk-print-id-cards"
            className="inline-flex items-center gap-1.5 rounded-md border-2 border-ncc-navy bg-ncc-gold px-2.5 py-1 industrial-label text-[10px] text-ncc-navy hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none"
          >
            <CreditCard className="h-3.5 w-3.5" />
            Print ID Cards{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
        </div>
      )}
      {workmen.map((w) => (
        <div
          key={w.id}
          data-testid={`workmen-inline-card-${w.id}`}
          className={`flex items-center gap-3 rounded-lg border bg-white p-3 transition hover:border-ncc-navy hover:shadow-sm ${selected.has(w.id) ? "border-ncc-navy ring-1 ring-ncc-gold/30" : "border-slate-200"}`}
        >
          {/* Checkbox */}
          {showActions && (
            <button
              type="button"
              onClick={() => toggle(w.id)}
              data-testid={`workmen-select-${w.id}`}
              aria-label={selected.has(w.id) ? "Deselect" : "Select"}
              className="shrink-0 text-ncc-navy hover:text-ncc-gold2"
            >
              {selected.has(w.id) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
            </button>
          )}

          {/* Avatar */}
          <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-full border-2 border-ncc-gold/40 bg-slate-100">
            {w.photo_base64 ? (
              <img src={w.photo_base64} alt={w.name} className="h-full w-full object-cover" />
            ) : (
              <User className="h-5 w-5 text-slate-400" />
            )}
          </div>

          {/* Main info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="truncate font-heading font-extrabold text-slate-900">{w.name}</div>
              {/* Approval status badge */}
              {(() => {
                const st = w.status || "pending";
                if (st === "approved") return (
                  <span title={w.approved_by_email ? `Approved by ${w.approved_by_email}` : "Approved"} className="inline-flex shrink-0 items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" data-testid={`workmen-status-${w.id}`}>
                    <BadgeCheck className="h-3 w-3" /> APPROVED
                  </span>
                );
                if (st === "rejected") return (
                  <span title={w.rejection_reason || "Rejected"} className="inline-flex shrink-0 items-center gap-0.5 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-700" data-testid={`workmen-status-${w.id}`}>
                    <Ban className="h-3 w-3" /> REJECTED
                  </span>
                );
                return (
                  <span title="Awaiting SAFETY-INDUCTION approval" className="inline-flex shrink-0 items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`workmen-status-${w.id}`}>
                    <Clock className="h-3 w-3" /> PENDING
                  </span>
                );
              })()}
              {/* De-Active state badge (only when set) */}
              {w.is_active === false && (
                <span
                  title={w.last_working_day ? `Last working day: ${w.last_working_day}` : "De-activated"}
                  className="inline-flex shrink-0 items-center gap-0.5 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700"
                  data-testid={`workmen-active-state-${w.id}`}
                >
                  <PowerOff className="h-3 w-3" /> DE-ACTIVE{w.last_working_day ? ` · LWD ${w.last_working_day}` : ""}
                </span>
              )}
              {(w.id_proof_type && w.id_proof_number) ? (
                <span
                  title={`${w.id_proof_type}: ${w.id_proof_number}`}
                  className="inline-flex shrink-0 items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700"
                >
                  <BadgeCheck className="h-3 w-3" /> {w.id_proof_type}
                </span>
              ) : w.aadhaar ? (
                <span
                  title={w.aadhaar}
                  className="inline-flex shrink-0 items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700"
                >
                  <BadgeCheck className="h-3 w-3" /> Aadhaar
                </span>
              ) : null}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[11px] text-slate-600">
              {w.trade && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 uppercase tracking-wider">{w.trade}</span>
              )}
              {w.age_years != null && <span>{w.age_years} yrs</span>}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[11px] text-slate-600">
              {w.experience_years ? <span>{w.experience_years}y exp</span> : null}
              {w.mobile && (
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {w.mobile}
                </span>
              )}
            </div>
            {(w.village || w.city || w.state) && (
              <div className="mt-0.5 flex items-center gap-1 font-mono text-[11px] text-slate-500">
                <MapPin className="h-3 w-3" />
                {[w.village, w.city, w.state].filter(Boolean).join(", ")}
              </div>
            )}
          </div>

          {/* Actions */}
          {showActions && (
            <div className="flex shrink-0 gap-1">
              {/* Approve / Reject — only for users with approve_workmen perm AND when pending */}
              {canApprove && (w.status || "pending") === "pending" && (
                <>
                  <button
                    type="button"
                    onClick={() => handleApprove(w)}
                    disabled={busyId === w.id}
                    data-testid={`workmen-approve-${w.id}`}
                    title="Approve (Safety Induction)"
                    className="rounded-md border-2 border-emerald-600 bg-emerald-50 p-1.5 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => { setRejectId(w.id); setRejectReason(""); }}
                    data-testid={`workmen-reject-${w.id}`}
                    title="Reject"
                    className="rounded-md border-2 border-red-500 bg-red-50 p-1.5 text-red-700 hover:bg-red-100"
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
              {/* 1) Form-XI Medical Certificate */}
              <button
                type="button"
                onClick={() => printFormXI(w, contractor)}
                disabled={(w.status || "pending") !== "approved"}
                data-testid={`workmen-inline-print-formxi-${w.id}`}
                className="rounded-md border border-emerald-300 bg-white p-1.5 text-emerald-700 hover:border-emerald-600 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-40"
                title={(w.status || "pending") !== "approved" ? "Awaiting SAFETY-INDUCTION approval" : "Print Form-XI · Medical Certificate (BOCW Rule 223(c))"}
              >
                <Stethoscope className="h-3.5 w-3.5" />
              </button>
              {/* 2) Worker's Screening Form */}
              <button
                type="button"
                onClick={() => printScreeningForm(w, contractor)}
                disabled={(w.status || "pending") !== "approved"}
                data-testid={`workmen-inline-print-screening-${w.id}`}
                className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:border-ncc-navy hover:text-ncc-navy disabled:cursor-not-allowed disabled:opacity-40"
                title={(w.status || "pending") !== "approved" ? "Awaiting SAFETY-INDUCTION approval" : "Print Worker's Screening Form"}
              >
                <ClipboardList className="h-3.5 w-3.5" />
              </button>
              {/* 3) Identity Card */}
              <button
                type="button"
                onClick={() => handlePrintId(w)}
                disabled={(w.status || "pending") !== "approved"}
                data-testid={`workmen-inline-print-id-${w.id}`}
                className="rounded-md border border-amber-300 bg-white p-1.5 text-amber-700 hover:border-amber-600 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-40"
                title={(w.status || "pending") !== "approved" ? "Awaiting SAFETY-INDUCTION approval" : "Print Identity Card (Front & Back)"}
              >
                <CreditCard className="h-3.5 w-3.5" />
              </button>
              {onEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(w)}
                  data-testid={`workmen-inline-edit-${w.id}`}
                  className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:border-ncc-navy hover:text-ncc-navy"
                  title="Edit"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {/* Active / De-Active toggle replaces Delete in contractor view */}
              {w.is_active === false ? (
                <button
                  type="button"
                  onClick={() => activateWorkman(w)}
                  disabled={busyId === w.id}
                  data-testid={`workmen-activate-${w.id}`}
                  title="Re-activate workman"
                  className="rounded-md border border-emerald-300 bg-white p-1.5 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                >
                  <Power className="h-3.5 w-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { setDeactivateTarget(w); setLwd(new Date().toISOString().slice(0, 10)); }}
                  data-testid={`workmen-deactivate-${w.id}`}
                  title="De-activate (record last working day)"
                  className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:border-slate-700 hover:text-slate-900"
                >
                  <PowerOff className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
      ))}

      {/* De-activate modal — collects Last Working Day */}
      {deactivateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setDeactivateTarget(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border-2 border-slate-900 bg-white shadow-brutal-lg" data-testid="workmen-deactivate-modal">
            <div className="border-b-2 border-slate-900 bg-slate-800 px-4 py-3 text-white">
              <div className="industrial-label">De-Activate Workman</div>
            </div>
            <div className="p-4 space-y-3">
              <div className="font-mono text-xs text-slate-700">
                You are marking <b>{deactivateTarget.name}</b> as <b>de-activated</b>. They will no longer be counted in your active roster
                and their ID Card prints will be disabled. Please record the last working day below.
              </div>
              <label className="block">
                <div className="industrial-label text-slate-600">Last Working Day <span className="text-red-600">*</span></div>
                <input
                  type="date"
                  value={lwd}
                  onChange={(e) => setLwd(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  data-testid="workmen-deactivate-lwd"
                  className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 border-t-2 border-slate-900 bg-slate-50 px-4 py-3">
              <button type="button" onClick={() => setDeactivateTarget(null)} className="border-2 border-slate-900 bg-white px-3 py-1.5 industrial-label text-[11px]">Cancel</button>
              <button type="button" onClick={submitDeactivate} disabled={!lwd || busyId === deactivateTarget.id} data-testid="workmen-deactivate-confirm" className="border-2 border-slate-800 bg-slate-800 px-3 py-1.5 industrial-label text-[11px] text-white disabled:opacity-50">Confirm De-Activate</button>
            </div>
          </div>
        </div>
      )}

      {/* Reject reason modal */}
      {rejectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setRejectId("")}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border-2 border-slate-900 bg-white shadow-brutal-lg" data-testid="workmen-reject-modal">
            <div className="border-b-2 border-slate-900 bg-red-600 px-4 py-3 text-white">
              <div className="industrial-label">Reject Workman</div>
            </div>
            <div className="p-4 space-y-2">
              <div className="font-mono text-xs text-slate-600">Reason (optional, shared with the contractor):</div>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                data-testid="workmen-reject-reason"
                className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                placeholder="e.g. Documents incomplete, blood group missing"
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t-2 border-slate-900 bg-slate-50 px-4 py-3">
              <button type="button" onClick={() => setRejectId("")} className="border-2 border-slate-900 bg-white px-3 py-1.5 industrial-label text-[11px]">Cancel</button>
              <button type="button" onClick={submitReject} disabled={busyId === rejectId} data-testid="workmen-reject-confirm" className="border-2 border-red-600 bg-red-600 px-3 py-1.5 industrial-label text-[11px] text-white disabled:opacity-50">Confirm Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
